import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cable, Lightbulb, Battery, ToggleLeft, Check } from 'lucide-react';

const CIRCUIT_LEVELS = [
  { id: 1, title: 'Simple Circuit', desc: 'Connect battery to bulb', components: ['battery', 'wire', 'bulb'], correct: ['battery', 'wire', 'bulb'], hint: 'A simple circuit needs: Battery -> Wire -> Bulb' },
  { id: 2, title: 'Switch Circuit', desc: 'Add a switch to control the bulb', components: ['battery', 'switch', 'wire', 'bulb'], correct: ['battery', 'wire', 'switch', 'wire', 'bulb'], hint: 'Include a switch between battery and bulb' },
  { id: 3, title: 'Series Circuit', desc: 'Connect two bulbs in series', components: ['battery', 'wire', 'bulb', 'bulb'], correct: ['battery', 'wire', 'bulb', 'wire', 'bulb'], hint: 'In series, bulbs are connected one after another' },
  { id: 4, title: 'Conductor Test', desc: 'Which material completes the circuit?', components: ['battery', 'wire', '???', 'bulb'], options: ['Rubber', 'Iron nail', 'Wood', 'Plastic'], correctOption: 'Iron nail', hint: 'Metals are good conductors of electricity' },
];

const CircuitConnect = () => {
  const navigate = useNavigate();
  const [level, setLevel] = useState(0);
  const [placed, setPlaced] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const current = CIRCUIT_LEVELS[level];

  const addComponent = (comp) => {
    if (feedback) return;
    setPlaced([...placed, comp]);
  };

  const checkCircuit = () => {
    if (current.options) {
      if (selectedOption === current.correctOption) {
        setScore(s => s + 20); setFeedback({ correct: true, msg: `Correct! ${current.correctOption} is a conductor!` });
      } else {
        setFeedback({ correct: false, msg: `${current.correctOption} is the conductor. ${current.hint}` });
      }
    } else {
      const isCorrect = placed.length >= current.components.length;
      if (isCorrect) {
        setScore(s => s + 20); setFeedback({ correct: true, msg: 'Circuit complete! The bulb lights up!' });
      } else {
        setFeedback({ correct: false, msg: `Hint: ${current.hint}` });
      }
    }
  };

  const nextLevel = () => {
    if (level < CIRCUIT_LEVELS.length - 1) { setLevel(l => l+1); setPlaced([]); setSelectedOption(''); setFeedback(null); }
    else setCompleted(true);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4"><Cable className="w-20 h-20 mx-auto text-yellow-500" /></div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Circuit Master!</h2>
          <p className="text-6xl font-black text-yellow-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {CIRCUIT_LEVELS.length * 20} points</p>
          <div className="space-y-3">
            <button onClick={() => { setLevel(0); setScore(0); setCompleted(false); setPlaced([]); setFeedback(null); }} className="w-full py-4 bg-yellow-500 text-white rounded-2xl font-bold border-b-4 border-yellow-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-amber-50" data-testid="circuit-connect-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-yellow-500 text-white px-4 py-2 rounded-2xl font-bold">Level {level+1}/{CIRCUIT_LEVELS.length}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl text-center">
          <h2 className="text-3xl font-black text-[#1E293B] mb-2">{current.title}</h2>
          <p className="text-lg text-[#64748B] mb-6">{current.desc}</p>

          {/* Circuit visualization */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-6 min-h-[120px]">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {placed.length === 0 && !current.options && <p className="text-[#64748B]">Click components below to build your circuit</p>}
              {placed.map((comp, i) => (
                <React.Fragment key={i}>
                  <div className={`px-4 py-3 rounded-xl font-bold text-white ${comp === 'battery' ? 'bg-green-500' : comp === 'bulb' ? 'bg-yellow-500' : comp === 'switch' ? 'bg-purple-500' : 'bg-slate-400'}`}>
                    {comp === 'battery' && <Battery className="w-6 h-6 inline mr-1" />}
                    {comp === 'bulb' && <Lightbulb className="w-6 h-6 inline mr-1" />}
                    {comp === 'switch' && <ToggleLeft className="w-6 h-6 inline mr-1" />}
                    {comp}
                  </div>
                  {i < placed.length - 1 && <span className="text-2xl text-[#64748B]">→</span>}
                </React.Fragment>
              ))}
              {feedback?.correct && <Lightbulb className="w-12 h-12 text-yellow-400 animate-pulse ml-4" />}
            </div>
          </div>

          {/* Component options or MCQ */}
          {current.options ? (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {current.options.map(opt => (
                <button key={opt} onClick={() => setSelectedOption(opt)} data-testid={`option-${opt}`}
                  className={`p-4 rounded-2xl border-2 font-bold transition-all ${selectedOption === opt ? 'border-yellow-500 bg-yellow-50' : 'border-slate-200 hover:border-yellow-400'}`}>{opt}</button>
              ))}
            </div>
          ) : (
            <div className="flex gap-3 justify-center flex-wrap mb-6">
              {['battery', 'wire', 'switch', 'bulb'].map(comp => (
                <button key={comp} onClick={() => addComponent(comp)} data-testid={`add-${comp}`}
                  className={`px-5 py-3 rounded-2xl border-2 font-bold hover:scale-105 transition-all ${comp === 'battery' ? 'bg-green-100 border-green-400 text-green-700' : comp === 'bulb' ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : comp === 'switch' ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-slate-100 border-slate-400 text-slate-700'}`}>+ {comp}</button>
              ))}
            </div>
          )}

          {feedback && <div className={`p-4 rounded-2xl font-bold mb-4 ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{feedback.msg}</div>}
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setPlaced([]); setSelectedOption(''); setFeedback(null); }} className="px-6 py-3 bg-slate-100 rounded-2xl font-bold text-[#64748B]">Reset</button>
            {feedback?.correct ? (
              <button onClick={nextLevel} className="px-8 py-3 bg-yellow-500 text-white rounded-2xl font-bold border-b-4 border-yellow-700 active:border-b-0 active:translate-y-1 transition-all">Next Level</button>
            ) : (
              <button onClick={checkCircuit} className="px-8 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all" data-testid="check-circuit-btn"><Check className="w-5 h-5 inline mr-1" />Check Circuit</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitConnect;
